import { useState, useEffect } from 'react';
import { MapPin, Mail, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

const DEFAULT_CONTACT = {
  coordinator: 'Bartłomiej Kochowicz',
  email: 'kuznialisewo@gmail.com',
  phone: '56 475 61 10',
};

const TOPIC_LABELS: Record<string, string> = {
  ogolne: 'Zapytanie ogólne',
  grant: 'Grant Działaj Lokalnie',
  szkolenie: 'Szkolenie MRG',
  wolontariat: 'Wolontariat',
  inne: 'Inne',
};

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { data: contact = DEFAULT_CONTACT } = useQuery({
    queryKey: ['contact-settings'],
    queryFn: async () => {
      const { data } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['contact.coordinator', 'contact.email', 'contact.phone']);
      const map = Object.fromEntries((data || []).map((r: any) => [r.key, r.value]));
      return {
        coordinator: map['contact.coordinator']?.trim() || DEFAULT_CONTACT.coordinator,
        email: map['contact.email']?.trim() || DEFAULT_CONTACT.email,
        phone: map['contact.phone']?.trim() || DEFAULT_CONTACT.phone,
      };
    },
    staleTime: 5 * 60 * 1000,
  });


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const newErrors: string[] = [];

    if (!name) newErrors.push('Pole „Imię i nazwisko" jest wymagane.');
    if (!email) newErrors.push('Pole „Email" jest wymagane.');
    if (!message) newErrors.push('Pole „Wiadomość" jest wymagane.');
    if (name.length > 100 || email.length > 255 || message.length > 2000) {
      newErrors.push('Co najmniej jedno pole jest za długie. Skróć dane.');
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (email && !emailOk) newErrors.push('Adres e-mail jest nieprawidłowy.');

    if (newErrors.length > 0) {
      setErrors(newErrors);
      toast({ title: 'Sprawdź formularz', description: newErrors[0], variant: 'destructive' });
      return;
    }
    setErrors([]);

    setSending(true);
    try {
      const idempotencyKey = `contact-${crypto.randomUUID()}`;
      const topicLabel = form.topic ? TOPIC_LABELS[form.topic] || form.topic : '';
      const { error } = await supabase.functions.invoke('send-contact-message', {
        body: {
          idempotencyKey,
          templateData: {
            name,
            email,
            topic: topicLabel,
            message,
          },
        },
      });
      if (error) throw error;
      toast({ title: 'Wiadomość wysłana!', description: 'Odpowiemy najszybciej jak to możliwe.' });
      setForm({ name: '', email: '', topic: '', message: '' });
    } catch (err) {
      console.error('Contact form send error', err);
      toast({
        title: 'Nie udało się wysłać wiadomości',
        description: 'Spróbuj ponownie za chwilę lub napisz bezpośrednio na kuznialisewo@gmail.com.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-16 lg:py-24 bg-background">
      <div className="container">
        <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-10 text-center">Kontakt</h2>
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <p className="text-sm">ul. Ks. Augustyna Łebińskiego 6, 86-230 Lisewo</p>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-secondary" />
                <a href={`mailto:${contact.email}`} className="text-sm hover:text-secondary">{contact.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary" />
                <a href={`tel:${contact.phone.replace(/\s+/g, '')}`} className="text-sm hover:text-secondary">{contact.phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-secondary" />
                <p className="text-sm">Koordynator ODL: {contact.coordinator}</p>
              </div>
            </div>

            <a href="https://facebook.com/kuzniainicjatywlokalnychwlisewie" target="_blank" rel="noopener noreferrer"
              aria-label="Facebook Kuźnia (otwiera się w nowej karcie)"
              className="inline-block bg-secondary text-secondary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:bg-secondary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              Facebook Kuźnia
            </a>

            <div className="bg-muted rounded-panel p-5 text-sm space-y-1">
              <p className="font-heading font-bold">Stowarzyszenie Kuźnia Inicjatyw Lokalnych w Lisewie</p>
              <p className="text-muted-foreground">KRS 0000149749</p>
              <p className="text-muted-foreground">ODL od 2015 · działalność od 2003</p>
            </div>

            <div className="bg-accent/20 rounded-panel p-5 border border-accent">
              <p className="font-heading font-bold text-sm mb-1">💛 Przekaż 1,5% podatku na Kuźnię — KRS 0000149749</p>
              <p className="text-xs text-muted-foreground">Wspierasz lokalne projekty za darmo!</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Formularz kontaktowy" noValidate>
            {errors.length > 0 && (
              <div role="alert" aria-live="polite" className="bg-destructive/10 border border-destructive text-destructive rounded-md p-3 text-sm">
                <p className="font-semibold mb-1">Sprawdź formularz:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            <div>
              <label htmlFor="contact-name" className="text-sm font-medium mb-1 block">
                Imię i nazwisko <span className="text-destructive" aria-label="pole wymagane">*</span>
              </label>
              <Input id="contact-name" autoComplete="name" required aria-required="true" aria-invalid={errors.some(e => e.includes('Imię'))} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-sm font-medium mb-1 block">
                Email <span className="text-destructive" aria-label="pole wymagane">*</span>
              </label>
              <Input id="contact-email" type="email" autoComplete="email" required aria-required="true" aria-invalid={errors.some(e => e.includes('Email') || e.includes('e-mail'))} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label htmlFor="contact-topic" className="text-sm font-medium mb-1 block">Temat</label>
              <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                <SelectTrigger id="contact-topic" aria-label="Temat wiadomości"><SelectValue placeholder="Wybierz temat" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ogolne">Zapytanie ogólne</SelectItem>
                  <SelectItem value="grant">Grant DL</SelectItem>
                  <SelectItem value="szkolenie">Szkolenie MRG</SelectItem>
                  <SelectItem value="wolontariat">Wolontariat</SelectItem>
                  <SelectItem value="inne">Inne</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="contact-message" className="text-sm font-medium mb-1 block">
                Wiadomość <span className="text-destructive" aria-label="pole wymagane">*</span>
              </label>
              <Textarea id="contact-message" rows={5} required aria-required="true" aria-invalid={errors.some(e => e.includes('Wiadomość'))} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button type="submit" disabled={sending} aria-busy={sending} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
              {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
